import * as _ from 'lodash';
import moment from 'moment';
import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Button,
  DropdownButton,
  MenuItem,
  Modal,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { RouteComponentProps } from 'react-router';

import { plural } from '../../../../shared/helpers/plural';
import { getReleaseGroupScope } from '../../../../shared/issuesV2';
import { Action } from '../../../../typings/permissions';
import { userHasPermissionForReleaseGroup } from '../../../helpers/permissions';
import { isDependencyCacheValid } from '../../../helpers/revisions';
import * as IssuesV2Store from '../../../redux/issuesV2';
import ProjectGroupsModule from '../../../redux/project_grouping';
import utils from '../../../utils';
import TooltipButton from '../../generic/Button/TooltipButton';
import Icon, { icons, IconText } from '../../generic/Icon';
import Link from '../../generic/Link/Link';
import Loader from '../../generic/Loader';
import { TeamAccessCard } from '../overview/ProjectSummaryV2/InfoCards/TeamAccessCard';
import type { IssueTypesSummary, Scope } from '../../../../shared/issuesV2';
import type { ReleaseGroupDetail } from '../../../../shared/releaseGroups';
import type { ProjectGroupStore } from '../../../redux/project_grouping/store';
import type { AsyncValue } from '../../../redux/asyncValue';
import type {
  APIError,
  IAsyncStatus,
  ProjectGroupRelease,
  ProjectGroupReleaseScan,
} from '../../../types';

import IssueListItem from './components/IssueListItem';
import { ProjectGroupReleaseSummaryActionCards } from './components/ProjectGroupReleaseSummaryActionCards';
import { ReleaseGroupUnknownDependencies } from './components/ReleaseGroupUnknownDependencies';

type Props = {
  projectGroup: ReleaseGroupDetail;
  releaseScans: ProjectGroupReleaseScan[];
  releaseSummary: {
    project_count: number;
    dependency_count: number;
    license_count: number;
    isSteady: boolean;
  };
  loading: boolean;
  error: APIError;
  //  Passed down from ProjectGroupContainer. This is undefined while a newly selected release loads.
  projectGroupRelease?: ProjectGroupRelease;
  selectedReleaseId: number;
  selectedScan?: ProjectGroupReleaseScan;
  scanStatus: IAsyncStatus<APIError>;
  releaseSummaryStatus: IAsyncStatus<APIError>;
  issueTypeSummary: AsyncValue<IssueTypesSummary>;
  issuesSortedByType: Array<[keyof IssueTypesSummary, number]> | null;
  typeSummaryTotalIssueCount: number | null;
} & RouteComponentProps<Params, {}> & DispatchProps;

type DispatchProps = {
  fetchReleaseScans: (projectGroupId: number, id: number) => Promise<void>;
  fetchReleaseSummary: (projectGroupId: number, id: number) => Promise<void>;
  scanRelease: (projectGroupId: number, id: number) => Promise<void>;
  selectScan: (scanId: number | null) => void;
  selectIssuesScanId: (scanId?: number) => void;
  fetchIssueTypeSummary: (scope: Scope) => Promise<void>;
};

type Params = {
  projectGroupId: number;
};

const formatRevisionScanTimestamp = (timestamp?: string) => moment(timestamp).format('MM/DD/YY h:mm a');

export const ProjectGroupReleaseSummaryPage = (props: Props) => {
  const [showSuggestUpgradeModal, setShowSuggestUpgradeModal] = useState(false);
  const { fetchReleaseScans, fetchIssueTypeSummary, selectIssuesScanId, router } = props;
  const { projectGroupId } = props.params;
  const { pathname } = props.location;
  const releaseId = props.projectGroupRelease?.id;
  const releaseProjectGroupId = props.projectGroupRelease?.projectGroupId;
  const selectedScanId = props.selectedScan?.id;
  //  Starts empty so the first render with a release counts as a release change and loads its data.
  const previousReleaseId = useRef<number>();
  const previousSelectedScanId = useRef(selectedScanId);

  /*
   * A layout effect, not a regular one, so it runs before the browser paints. Until the fetch below starts, nothing
   * reads as loading (no scans, idle statuses), so a regular effect lets the first paint flash the "Ready to Scan" view.
   */
  useLayoutEffect(() => {
    const hasReleaseChanged = releaseId !== previousReleaseId.current;
    const hasScanChanged = selectedScanId !== previousSelectedScanId.current;
    previousReleaseId.current = releaseId;
    previousSelectedScanId.current = selectedScanId;
    if (!releaseId || !releaseProjectGroupId) {
      return;
    }
    const scope = getReleaseGroupScope(releaseProjectGroupId.toString(), releaseId, selectedScanId);
    //  Compared by id, since the release is refetched periodically while its dependencies are determined.
    if (hasReleaseChanged) {
      const fetchData = async () => {
        await fetchReleaseScans(projectGroupId, releaseId);
        /*
         * The release or scan can change, or the page unmount, while the scans load (loading them can select a
         * scan). Whatever changed already fetched its own summary, and this older scope must not land after it.
         */
        if (previousReleaseId.current !== releaseId || previousSelectedScanId.current !== selectedScanId) {
          return;
        }
        await fetchIssueTypeSummary(scope);
      };
      void fetchData();
    } else if (hasScanChanged) {
      if (selectedScanId) {
        selectIssuesScanId(selectedScanId);
        router.push({ pathname, search: `?releaseScanId=${selectedScanId}` });
      }
      void fetchIssueTypeSummary(scope);
    }
  }, [fetchReleaseScans, fetchIssueTypeSummary, selectIssuesScanId, router, pathname, projectGroupId, releaseProjectGroupId, releaseId, selectedScanId]);

  //  Clears the release on unmount, so a release load still in flight doesn't fetch its summary after the page is gone.
  useLayoutEffect(() => () => {
    previousReleaseId.current = undefined;
  }, []);

  //  With no scan selected, issues are shown for the most recently scanned scan, which is how the issues query picks
  //  it. It isn't selected in the store, since that would change the issues scope and refetch the same issues.
  //  `scanned_at` is an ISO timestamp, so comparing the strings orders them by time.
  const displayedScan = props.selectedScan ?? _.maxBy(props.releaseScans, 'scanned_at');

  const renderRevisionScans = () => props.releaseScans.map(
    scan => (
      <MenuItem
        key={scan.id}
        active={scan.id === displayedScan?.id}
        eventKey={scan.id}
      >
        {formatRevisionScanTimestamp(scan.scanned_at)}
      </MenuItem>
    ),
  );

  const onShowLimitedScanDetailInfo = () => {
    setShowSuggestUpgradeModal(true);
  };

  const onHideLimitedScanDetailInfo = () => {
    setShowSuggestUpgradeModal(false);
  };

  const onScanRelease = async () => {
    const { projectGroup, projectGroupRelease, scanRelease, fetchReleaseSummary } = props;
    if (!projectGroupRelease) {
      return;
    }
    await scanRelease(projectGroup.id, projectGroupRelease.id);
    //  The scan updates the release's issue counts.
    await fetchReleaseSummary(projectGroup.id, projectGroupRelease.id);
  };

  const onScanSelect = (scanId: unknown) => {
    if (typeof scanId === 'number') {
      props.selectScan(scanId);
    }
  };

  const renderIssues = ({ dependency_count: dependencies, license_count: licenses }: Props['releaseSummary']) => {
    const isScanned = props.releaseScans.length > 0;
    const { issuesSortedByType, typeSummaryTotalIssueCount } = props;
    const scan = displayedScan;
    let issueCount = 0;
    let renderedIssues: ReactElement[] = [];

    if (scan && issuesSortedByType) {
      issueCount = typeSummaryTotalIssueCount || 0;
      renderedIssues = issuesSortedByType.map(([issueType, count]) => (
        <IssueListItem
          key={issueType}
          issueType={issueType}
          issuesCount={count}
          projectGroup={props.projectGroup}
          router={props.router}
        />
      ));
    }
    const btnLod = !utils.verify_subscription('premium') && (
      <Button
        className="btn btn-increase-detail btn-link"
        onClick={onShowLimitedScanDetailInfo}
      >
        <IconText
          icon={icons.warning}
          text="Scan depth is limited"
        />
      </Button>
    );
    const issueHeader = (
      <b className="inline-header">
        Issues in {props.projectGroupRelease?.title ?? '...'}
        {props.releaseScans.length > 0 && scan && (
          <span>
            <Icon icon={icons.angleRight} />
            <DropdownButton
              id="select-release-scan"
              className="dropdown-mid choose-scan inline-header"
              key={scan.id}
              title={`Scanned: ${formatRevisionScanTimestamp(scan.scanned_at)}`}
              onSelect={onScanSelect}
            >
              {renderRevisionScans()}
            </DropdownButton>
          </span>
        )}
      </b>
    );

    const canEditReleaseGroup = userHasPermissionForReleaseGroup(Action.Edit, props.projectGroup.id);
    const allDependencyCacheValid = props.projectGroupRelease?.projects?.every(
      project =>
        isDependencyCacheValid(project.revision),
    );
    if (!isScanned) {
      return (
        <div>
          <div className="project-summary-header">
            <div className="row">
              <div className="col-md-7">
                {issueHeader}
                <h4>Not Scanned Yet</h4>
              </div>
              <div className="col-md-5 text-right">
                {btnLod}
              </div>
            </div>
          </div>
          <ReleaseGroupUnknownDependencies
            projectGroup={props.projectGroup}
            releaseId={props.selectedReleaseId}
            standalone
          />
          <div className="text-center margin-top">
            <div className="card noborder margin-top">
              <br />
              <span className="margin-top" style={{ fontSize: '65px' }}>
                <Icon icon={icons.cube} />
              </span>
              <h3>Ready to Scan</h3>
              <p>
                FOSSA has discovered <b>{licenses ?? '...'}</b> Licenses and<br />
                <b>{dependencies ?? '...'}</b> Dependencies in this version.
              </p>
              <TooltipScanButton
                onScanRelease={onScanRelease}
                canEditReleaseGroup={canEditReleaseGroup}
                allDependencyCacheValid={allDependencyCacheValid}
                />
            </div>
          </div>
        </div>
      );
    }

    const lastScan = _.head(props.releaseScans);
    let btnScan;
    if (canEditReleaseGroup && allDependencyCacheValid) {
      btnScan = (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="lastScanTooltip">
              <b>Last policy scan:</b> {lastScan ? moment(lastScan.scanned_at).fromNow() : 'Unknown'}
            </Tooltip>
          }>
          <a className="clickable btn-scan" onClick={onScanRelease}>
            <IconText
              icon={icons.reload}
              text="Run policy scan"
            />
          </a>
        </OverlayTrigger>
      );
    } else {
      const tooltipMessage = allDependencyCacheValid ? 'You are not permitted to edit this release group.' : 'FOSSA is still determining the dependencies for this revision';
      btnScan = (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="cannotScan">
              {tooltipMessage}
            </Tooltip>
          }>
          <a className="clickable btn-scan">
            <IconText
              icon={icons.reload}
              text="Run policy scan"
            />
          </a>
        </OverlayTrigger>
      );
    }

    return issueCount > 0 ? (
      <div>
        <div className="project-summary-header project-summary-failed">
          <div className="row">
            <div className="col-md-7">
              {issueHeader}
              <h4>
                {issueCount} {plural(issueCount, 'Issue')}
                {btnScan}
              </h4>
            </div>
            <div className="col-md-5 text-right">
              {btnLod}
              {/* TODO: add icons for "license protection", "security", etc... "ENABLED" */}
            </div>
          </div>
        </div>
        <div className="padded padded-small">
          <ul className="project-issues-list">
            {renderedIssues}
            <ReleaseGroupUnknownDependencies
              projectGroup={props.projectGroup}
              releaseId={props.selectedReleaseId}
            />
          </ul>
        </div>
      </div>
    ) : (
      <div>
        <div className="project-summary-header project-summary-success">
          <div className="row">
            <div className="col-md-7">
              {issueHeader}
              <h4>
                <IconText
                  icon={icons.check}
                  text="All Checks Passed"
                />
                {btnScan}
              </h4>
            </div>
            <div className="col-md-5 text-right">
              {btnLod}
            </div>
          </div>
        </div>
        <ReleaseGroupUnknownDependencies
          projectGroup={props.projectGroup}
          releaseId={props.selectedReleaseId}
          standalone
        />
        <div className="text-center card card-success noborder margin-top">
          <br />
          <span className="margin-top" style={{ fontSize: '65px' }}>
            <Icon icon={icons.review} />
          </span>
          <br />
          <br />
          <p className="subtitle">
            FOSSA ran license checks across <b>{dependencies}</b> dependencies.
            {issueCount === 0 && (
              <div>No issues were found.</div>
            )}
          </p>
          <Button
            className="clickable"
            href="/reports"
          >
            <IconText
              icon={icons.addToProject}
              text="Generate Report"
            />
          </Button>
        </div>
      </div>
    );
  };

  //  Only the issues panel shows a loader, so the cards beside it aren't remounted (and refetched) on every load.
  const isLoadingIssues = !props.projectGroupRelease || props.scanStatus.loading || props.issueTypeSummary.isLoading;
  const baseUrl = `/projects/group/${props.projectGroup.id}`;
  const stats = props.releaseSummary;
  return (
    <div className="container margin-top">
      {props.scanStatus.error?.message && (
        <div role="alert" className="alert alert-error alert-dismissable">
          Error scanning release: {props.scanStatus.error.message}
        </div>
      )}
      {props.releaseSummaryStatus.error?.message && (
        <div role="alert" className="alert alert-error alert-dismissable">
          Error retrieving release summary: {props.releaseSummaryStatus.error.message}
        </div>
      )}
      <div className="animated fadeInUp quick project_info">
        <div className="row project_info__container">
          <div className="col-md-8 project_info__summary">
            <div className="panel panel-flat project_info__issues_panel">
              {isLoadingIssues ? <Loader /> : renderIssues(stats)}
            </div>
          </div>
          <div className="col-md-4 project_info__action_cards">
            <ProjectGroupReleaseSummaryActionCards
              baseUrl={baseUrl}
              projectGroupId={props.projectGroup.id}
              selectedReleaseId={props.selectedReleaseId}
              projectCount={stats.project_count}
              dependencyCount={stats.dependency_count}
              licenseCount={stats.license_count}
              isReleaseSummaryStatusLoading={props.releaseSummaryStatus.loading}
            />
            <TeamAccessCard
              releaseGroupId={props.projectGroup.id}
              isLoading={props.releaseSummaryStatus.loading}
            />
          </div>
        </div>
      </div>
      <Modal show={showSuggestUpgradeModal} backdrop onHide={onHideLimitedScanDetailInfo}>
        <Modal.Header closeButton>
          <Modal.Title>Scan depth for this project is limited</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Upgrade to the business plan to unlock additional features.</p>
          <Link to="/account/settings/billing" className="btn btn-primary margin-bottom-sm">Subscription Settings
          </Link>
        </Modal.Body>
      </Modal>
    </div>
  );
};

const TooltipScanButton = ({ onScanRelease, canEditReleaseGroup, allDependencyCacheValid }) => {
  const isDisabled = !canEditReleaseGroup || !allDependencyCacheValid;
  const tooltipMessage = !canEditReleaseGroup
    ? 'You are not permitted to edit this release group.'
    : 'FOSSA is still determining dependencies for one of the projects.';
  return (
    <TooltipButton
      onClick={onScanRelease}
      tooltip={tooltipMessage}
      showTooltip={isDisabled}
      isDisabled={isDisabled}
    >
      <IconText
        icon={icons.reload}
        text="Run Issue Scan"
      />
    </TooltipButton>
  );
};

const mapStateToProps = createStructuredSelector({
  releaseSummary: ProjectGroupsModule.selectors.getReleaseSummary,
  releaseSummaryStatus: ProjectGroupsModule.selectors.getReleaseSummaryStatus,
  releaseScans: ProjectGroupsModule.selectors.getReleaseScans,
  scanStatus: ProjectGroupsModule.selectors.getReleaseScanStatus,
  selectedScan: ProjectGroupsModule.selectors.getSelectedScan,
  selectedReleaseId: (state: { projectGroups: ProjectGroupStore }, { projectGroup }: Pick<Props, 'projectGroup'>) =>
    ProjectGroupsModule.selectors.getSelectedReleaseIdForGroup(state, projectGroup.id),
  issueTypeSummary: IssuesV2Store.selectors.getIssueTypeSummary,
  issuesSortedByType: IssuesV2Store.selectors.getSortedIssueTypeSummary,
  typeSummaryTotalIssueCount: IssuesV2Store.selectors.getTotalIssuesFromTypeSummary,
});

const mapDispatchToProps = {
  scanRelease: ProjectGroupsModule.actions.scanRelease,
  fetchReleaseScans: ProjectGroupsModule.actions.fetchReleaseScans,
  fetchReleaseSummary: ProjectGroupsModule.actions.fetchReleaseSummary,
  selectScan: ProjectGroupsModule.actions.selectScan,
  selectIssuesScanId: IssuesV2Store.actions.setScanId,
  fetchIssueTypeSummary: IssuesV2Store.actions.fetchIssueTypeSummary,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectGroupReleaseSummaryPage);
